import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";

const TYPES = ["owner_auth", "sale_contract"];
const cls = "w-full rounded-lg border px-3 py-2 text-sm";

/** Admin: branding used on printed forms, numbering (prefix + next number), terms in English and Arabic, default fine. */
export function FormsSettingsPanel() {
  const { t } = useT();
  const [s, setS] = useState(null);
  const [num, setNum] = useState({});
  useEffect(() => {
    Promise.all([api.get("/settings"), api.get("/showroom-forms/numbering"), api.get("/showroom-forms/texts")]).then(([a, b, c]) => {
      const d = a.data;
      const v = { showroom_name_ar: d.showroom_name_ar || "", cr_number: d.cr_number || "", branch: d.branch || "", address_ar: d.address_ar || "",
        contract_return_fine_default: d.contract_return_fine_default || 0, sale_contract_manager_signature: d.sale_contract_manager_signature || "not_used" };
      TYPES.forEach((tp) => ["terms_en", "terms_ar"].forEach((k) => { v[`${tp}_${k}`] = (c.data[tp][k] || []).join("\n"); }));
      TYPES.forEach((tp) => ["remark_en", "remark_ar"].forEach((k) => { v[`${tp}_${k}`] = c.data[tp][k] || ""; }));
      setS(v); setNum(b.data);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);
  if (!s) return null;
  const save = async () => {
    try {
      const body = { ...s, contract_return_fine_default: Number(s.contract_return_fine_default || 0) };
      TYPES.forEach((tp) => ["terms_en", "terms_ar"].forEach((k) => { body[`${tp}_${k}`] = s[`${tp}_${k}`].split("\n").map((x) => x.trim()).filter(Boolean); }));
      await api.put("/settings", body);
      for (const tp of TYPES) await api.post("/showroom-forms/numbering", { form_type: tp, prefix: num[tp].prefix, next_number: Number(num[tp].next_number) });
      toast.success(t("sf.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const field = (k, label, area, dir) => (
    <label key={k} className="text-sm block">{label}
      {area ? <textarea data-testid={`fs-${k}`} dir={dir} rows={5} className={cls} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} />
        : <input data-testid={`fs-${k}`} dir={dir} className={`${cls} h-11`} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} />}
    </label>
  );
  return (
    <div className="space-y-5" data-testid="forms-settings">
      <div className="grid gap-3 sm:grid-cols-2">
        {field("showroom_name_ar", t("fs.name_ar"), false, "rtl")}
        {field("cr_number", t("fs.cr"))}
        {field("branch", t("fs.branch"))}
        {field("address_ar", t("fs.address_ar"), false, "rtl")}
        {field("contract_return_fine_default", t("fs.fine_default"))}
        <label className="text-sm block">{t("fs.manager_sig")}
          <select data-testid="fs-manager-sig" className={`${cls} h-11`} value={s.sale_contract_manager_signature}
            onChange={(e) => setS({ ...s, sale_contract_manager_signature: e.target.value })}>
            <option value="not_used">{t("fs.manager_sig_not_used")}</option>
            <option value="required">{t("fs.manager_sig_required")}</option>
          </select>
        </label>
      </div>
      {TYPES.map((tp) => (
        <div key={tp} className="space-y-3 border-t pt-4">
          <div className="font-semibold">{tp === "owner_auth" ? t("sf.owner_title") : t("sf.contract_title")}</div>
          <div className="flex gap-3 flex-wrap">
            <label className="text-sm">{t("fs.prefix")}<input data-testid={`fs-${tp}-prefix`} className={`${cls} h-11`} value={num[tp].prefix} onChange={(e) => setNum({ ...num, [tp]: { ...num[tp], prefix: e.target.value } })} /></label>
            <label className="text-sm">{t("fs.next_no")}<input data-testid={`fs-${tp}-next`} inputMode="numeric" className={`${cls} h-11`} value={num[tp].next_number} onChange={(e) => setNum({ ...num, [tp]: { ...num[tp], next_number: e.target.value } })} /></label>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {field(`${tp}_terms_en`, t("fs.terms_en"), true, "ltr")}
            {field(`${tp}_terms_ar`, t("fs.terms_ar"), true, "rtl")}
            {field(`${tp}_remark_en`, t("fs.remark_en"), true, "ltr")}
            {field(`${tp}_remark_ar`, t("fs.remark_ar"), true, "rtl")}
          </div>
        </div>
      ))}
      <button data-testid="fs-save" className="btn-primary h-11 px-6" onClick={save}>{t("sf.save")}</button>
    </div>
  );
}
